// signal-form-field.ts
import { computed, signal, Signal } from '@angular/core';

export interface iSignalFormField<T> {
  value: Signal<T>;
  displayValue: Signal<string>;
  dirty: Signal<boolean>;
  touched: Signal<boolean>;
  valid: Signal<boolean>;
  errors: Signal<string[]>;
  showErrors: Signal<boolean>;

  setValue: (value: T) => void;
  setDisplayValue: (value: string) => void;
  setTouched: () => void;
  reset: () => void;
}

export function SignalFormField<T>(
  initial: T,
  validator?: (value: T) => string[],
  formatter?: (value: T) => string,
  parser?: (displayValue: string, currentValue: T) => T
): iSignalFormField<T> {
  const internalValue = signal<T>(initial);
  const touched = signal(false);

  const validate = (value: T): string[] =>
    validator ? validator(value) : [];

  const format = (value: T): string =>
    formatter ? formatter(value) : String(value ?? '');

  const parse = (displayValue: string, currentValue: T): T =>
    parser
      ? parser(displayValue, currentValue)
      : (displayValue as unknown as T);

  const dirty = computed(() => internalValue() !== initial);
  const errors = computed(() => validate(internalValue()));
  const valid = computed(() => errors().length === 0);
  const showErrors = computed(() => touched() && errors().length > 0);
  const displayValue = computed(() => format(internalValue()));

  return {
    value: computed(() => internalValue()),
    displayValue,
    dirty,
    touched,
    valid,
    errors,
    showErrors,

    setValue: (val: T) => internalValue.set(val),
    setDisplayValue: (value: string) => {
      internalValue.set(parse(value, internalValue()));
    },

    setTouched: () => touched.set(true),
    reset: () => {
      internalValue.set(initial);
      touched.set(false);
    }
  };
}
